import { memo, useCallback, useMemo, useState } from 'react'
import { PageHead } from '@components/PageHead'
import { useFolders } from '@features/vault/hooks/useFolders'
import {
  useVaultList,
  VAULT_FILTER_VALUES,
  type VaultFilter,
} from '@features/vault/hooks/useVaultList'
import { useVaultListEntries } from '@features/vault/hooks/useVaultListEntries'
import type { EquivalentDomainGroupWire } from '@features/equivalent-domains/api/equivalentDomainsApi'
import { VaultFolderSelect } from '@features/vault/components/VaultFolderSelect'
import { VaultItemAvatar } from '@features/vault/components/VaultItemAvatar'
import { VaultStrengthBadge } from '@features/vault/components/VaultStrengthBadge'
import { disambiguateLoginLabels } from '@features/vault/services/disambiguateLoginLabels'
import { deleteVaultItemById } from '@features/vault/services/vaultFlow'
import { type VaultItem } from '@features/vault/types'
import { copyToClipboard, getEffectiveClipboardTtlSec } from '@services/clipboard'
import { formatDateJst } from '@utils/datetime'
import { EmptyState } from '@shared/ui/EmptyState'
import { Notice } from '@shared/ui/Notice'
import { ScrollTopButton } from '@shared/ui/ScrollTopButton'
import { useToast } from '@shared/ui/useToast'
import { Icon } from '@/icons/Icon'
import { useTranslation } from '@/i18n'

/**
 * 保管庫 一覧画面 (T-D-Vault-A、xlsx No.27、L-6 vault route 解消の一段目)。
 *
 * design/js/screens-vault.jsx (VaultList) を移植。
 *   - filter chips: all / favorite / login / card / note / identity
 *   - folder chips: フォルダ絞込 (T-D-Vault-Folder Phase 4)
 *   - row actions: copy username / copy password / 詳細を開く
 *   - clipboard 90s 自動クリア
 *   - empty / filtered-empty / decryption-warning の 3 fallback state
 */

interface VaultListPageProps {
  activeFolderId?: string | null
  /** Topbar 検索 input の現在値 (T-E1)。empty なら text filter なし */
  searchQuery?: string
}

const FILTER_ICON: Record<
  VaultFilter,
  'vault' | 'star' | 'globe' | 'credit-card' | 'file-text' | 'id-card'
> = {
  all: 'vault',
  favorite: 'star',
  login: 'globe',
  card: 'credit-card',
  note: 'file-text',
  identity: 'id-card',
}

/**
 * Vault 一覧の subtitle 文字列を返す。
 *
 *   - login: ookey-browser parity でユーザー名 (空なら `(ユーザー名なし)`) のみを返す。
 *     URL を subtitle に出すと vault-name (例: "ookey.jp") と重複する UX があったため、
 *     ホスト名は使わない。`duplicateSuffix` (`#2`, `#3`) は呼出側で付与する。
 *   - card: 名義人。
 *   - note: 本文先頭 60 文字。
 *   - identity: 姓名。
 */
const previewFor = (item: VaultItem, noUsernameLabel: string): string => {
  const p = item.payload
  if (p.type === 'login') {
    const u = (p.username ?? '').trim()

    return u === '' ? noUsernameLabel : u
  }
  if (p.type === 'card') return p.cardHolder ?? ''
  if (p.type === 'note') return (p.body ?? '').slice(0, 60).replace(/\n+/gu, ' ')
  if (p.type === 'identity') return [p.firstName, p.lastName].filter(Boolean).join(' ')

  return ''
}

const formatDate = formatDateJst

interface VaultItemRowProps {
  item: VaultItem
  /** ドメイングループ展開時の入れ子行か。 */
  nested: boolean
  isSelected: boolean
  /** 解決済フォルダ名 (未所属 / フォルダ削除済なら null)。 */
  folderName: string | null
  /** 同一 subtitle (username or 「ユーザー名なし」) を持つ 2 件目以降に付与する識別子 (例: ` #2`)。
   *  1 件目 / non-login は空文字。subtitle 文字列の末尾に連結される。 */
  duplicateSuffix: string
  onToggleSelect: (id: string) => void
  onCopyUsername: (item: VaultItem) => void
  onCopyPassword: (item: VaultItem) => void
  onOpenDetail: (id: string) => void
  onOpenEdit: (id: string) => void
}

/**
 * 保管庫 1 アイテムの行 (L-29 #2.2)。
 *
 * `React.memo` 化することで、検索キーストローク / 選択トグル / modal 開閉などで
 * `VaultListPage` が再描画されても、props (item 参照 / isSelected / folderName /
 * 安定コールバック) が変わらない行は再描画をスキップする。`useVaultList` の
 * `filtered` は `useMemo` 済なので残存 item の参照は安定する。
 */
const VaultItemRow = memo(
  ({
    item,
    nested,
    isSelected,
    folderName,
    duplicateSuffix,
    onToggleSelect,
    onCopyUsername,
    onCopyPassword,
    onOpenDetail,
    onOpenEdit,
  }: VaultItemRowProps): JSX.Element => {
    const { t } = useTranslation()
    const preview = previewFor(item, t('vault.list.row.no_username')) + duplicateSuffix

    return (
      <li
        className={`list-row vault-list-row${nested ? ' vault-list-row--nested' : ''}${
          isSelected ? ' is-selected' : ''
        }`}
        role="button"
        tabIndex={0}
        onClick={() => onOpenDetail(item.id)}
        onKeyDown={ev => {
          if (ev.key === 'Enter' || ev.key === ' ') {
            ev.preventDefault()
            onOpenDetail(item.id)
          }
        }}
        aria-label={item.payload.name}
      >
        <div
          className="vault-row-select"
          onClick={ev => ev.stopPropagation()}
          onKeyDown={ev => ev.stopPropagation()}
          role="presentation"
        >
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggleSelect(item.id)}
            aria-label={item.payload.name}
            data-testid={`vault-select-${item.id}`}
          />
        </div>
        <VaultItemAvatar name={item.payload.name} className="vault-row-icon" />
        <div className="vault-row-main">
          <div className="nm">
            {item.payload.name}
            {item.payload.favorite === true && (
              <span className="vault-row-fav" aria-hidden="true">
                <Icon name="star" />
              </span>
            )}
          </div>
          {preview && <div className="sb">{preview}</div>}
        </div>
        <div className="vault-row-tag">
          <span className="tag">{t(`vault.type.${item.type}`)}</span>
          <VaultStrengthBadge item={item} />
          {folderName !== null && (
            <span className="tag vault-row-folder-tag">
              <Icon name="folder" aria-hidden="true" />
              {folderName}
            </span>
          )}
        </div>
        <div className="vault-row-updated sb mono">
          {t('vault.list.row.updated_prefix')} {formatDate(item.updatedAt)}
        </div>
        <div
          className="acts vault-row-acts"
          onClick={ev => ev.stopPropagation()}
          onKeyDown={ev => ev.stopPropagation()}
          role="presentation"
        >
          {item.payload.type === 'login' && item.payload.username && (
            <button
              type="button"
              className="icon-btn sm"
              title={t('vault.list.row.copy_username')}
              aria-label={t('vault.list.row.copy_username')}
              onClick={() => onCopyUsername(item)}
            >
              <Icon name="user" />
            </button>
          )}
          {item.payload.type === 'login' && item.payload.password && (
            <button
              type="button"
              className="icon-btn sm"
              title={t('vault.list.row.copy_password')}
              aria-label={t('vault.list.row.copy_password')}
              onClick={() => onCopyPassword(item)}
            >
              <Icon name="copy" />
            </button>
          )}
          <button
            type="button"
            className="icon-btn sm vault-row-edit"
            title={t('vault.detail.edit')}
            aria-label={t('vault.detail.edit')}
            onClick={() => onOpenEdit(item.id)}
            data-testid={`vault-row-edit-${item.id}`}
          >
            <Icon name="edit" />
          </button>
          <button
            type="button"
            className="icon-btn sm"
            title={t('vault.list.row.open_detail')}
            aria-label={t('vault.list.row.open_detail')}
            onClick={() => onOpenDetail(item.id)}
          >
            <Icon name="chevron-right" />
          </button>
        </div>
      </li>
    )
  },
)

VaultItemRow.displayName = 'VaultItemRow'

export const VaultListPage = ({
  activeFolderId = null,
  searchQuery = '',
}: VaultListPageProps): JSX.Element => {
  const { t } = useTranslation()
  const { show: showToast } = useToast()
  const { folders, createFolder, updateFolder, deleteFolder } = useFolders()

  // 行描画ごとの `folders.find()` 線形探索 (item 数 × folder 数) を避けるため
  // id→folder の Map を 1 度だけ作る (P1-C 2.1)。
  const folderById = useMemo(() => new Map(folders.map(f => [f.id, f])), [folders])

  const { state, filter, setFilter, filtered, counts, reload } = useVaultList({
    activeFolderId,
    searchQuery,
  })

  // ドメインルールによる行統合 (#settings/domain + 環境設定「同等ドメインを統合表示」)。
  const entries = useVaultListEntries(filtered)

  // ookey-browser parity: 同じ username (or 「ユーザー名なし」) を持つ login が並んだとき
  // subtitle 末尾に ` #2`, ` #3` を付けて識別できるようにする。`filtered` 全体で
  // 算出するので、ドメイングループ展開で nested 行になっても番号が一貫する。
  const duplicateSuffixMap = useMemo(() => disambiguateLoginLabels(filtered), [filtered])
  const [expandedGroups, setExpandedGroups] = useState<ReadonlySet<string>>(new Set())

  const toggleGroup = useCallback((id: string): void => {
    setExpandedGroups(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)

      return next
    })
  }, [])

  // F4: フォルダ作成 modal の state
  const [folderModalOpen, setFolderModalOpen] = useState(false)
  const [folderDraft, setFolderDraft] = useState('')
  const [folderSubmitting, setFolderSubmitting] = useState(false)
  const [folderError, setFolderError] = useState<string | null>(null)

  // #1 + #2: フォルダ管理 modal (rename / delete) の state
  const [folderManageOpen, setFolderManageOpen] = useState(false)
  const [folderDrafts, setFolderDrafts] = useState<Record<string, string>>({})

  const [folderRowBusy, setFolderRowBusy] = useState<
    Record<string, 'saving' | 'deleting' | undefined>
  >({})

  const [folderManageError, setFolderManageError] = useState<string | null>(null)

  const [folderDeleteTarget, setFolderDeleteTarget] = useState<{ id: string; name: string } | null>(
    null,
  )

  // 一括選択 → 一括削除 (vault list quick-delete)。削除は soft-delete (ゴミ箱へ)。
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set())
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false)
  const [bulkDeleting, setBulkDeleting] = useState(false)

  const toggleSelect = useCallback((id: string): void => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)

      return next
    })
  }, [])

  const onCreateFolder = useCallback(async (): Promise<void> => {
    if (folderSubmitting) return
    const name = folderDraft.trim()

    if (!name) {
      setFolderError('vault.folder.error.invalid_name')

      return
    }
    setFolderSubmitting(true)
    setFolderError(null)
    try {
      await createFolder(name)
      setFolderDraft('')
      setFolderModalOpen(false)
    } catch {
      setFolderError('vault.error.generic')
    } finally {
      setFolderSubmitting(false)
    }
  }, [createFolder, folderDraft, folderSubmitting])

  const openManageFolders = useCallback((): void => {
    const drafts: Record<string, string> = {}
    for (const f of folders) drafts[f.id] = f.name
    setFolderDrafts(drafts)
    setFolderRowBusy({})
    setFolderManageError(null)
    setFolderDeleteTarget(null)
    setFolderManageOpen(true)
  }, [folders])

  const onRenameFolder = useCallback(
    async (id: string): Promise<void> => {
      const current = folders.find(f => f.id === id)
      const next = (folderDrafts[id] ?? '').trim()
      if (!current) return
      if (!next) {
        setFolderManageError('vault.folder.error.invalid_name')

        return
      }
      if (next === current.name) return
      setFolderRowBusy(prev => ({ ...prev, [id]: 'saving' }))
      setFolderManageError(null)
      try {
        await updateFolder(id, next)
      } catch {
        setFolderManageError('vault.folder.error.update_failed')
      } finally {
        setFolderRowBusy(prev => ({ ...prev, [id]: undefined }))
      }
    },
    [folders, folderDrafts, updateFolder],
  )

  const onDeleteFolder = useCallback(
    async (id: string): Promise<void> => {
      setFolderRowBusy(prev => ({ ...prev, [id]: 'deleting' }))
      setFolderManageError(null)
      try {
        await deleteFolder(id)
        setFolderDrafts(prev => {
          const next = { ...prev }

          delete next[id]

          return next
        })
        setFolderDeleteTarget(null)
        if (activeFolderId === id) {
          window.location.hash = 'vault'
        }
      } catch {
        setFolderManageError('vault.folder.error.delete_failed')
      } finally {
        setFolderRowBusy(prev => ({ ...prev, [id]: undefined }))
      }
    },
    [deleteFolder, activeFolderId],
  )

  const onCopyUsername = useCallback(
    async (item: VaultItem): Promise<void> => {
      if (item.payload.type !== 'login') return
      const value = item.payload.username
      if (!value) return
      try {
        await copyToClipboard(value)
        showToast({
          titleKey: 'vault.toast.copied_username',
          titleVars: { ttl: getEffectiveClipboardTtlSec() },
          tone: 'success',
          dedupe: true,
        })
      } catch {
        showToast({ titleKey: 'vault.toast.copy_failed', tone: 'danger', dedupe: true })
      }
    },
    [showToast],
  )

  const onCopyPassword = useCallback(
    async (item: VaultItem): Promise<void> => {
      if (item.payload.type !== 'login') return
      const value = item.payload.password
      if (!value) return
      try {
        await copyToClipboard(value)
        showToast({
          titleKey: 'vault.toast.copied_password',
          titleVars: { ttl: getEffectiveClipboardTtlSec() },
          tone: 'success',
          dedupe: true,
        })
      } catch {
        showToast({ titleKey: 'vault.toast.copy_failed', tone: 'danger', dedupe: true })
      }
    },
    [showToast],
  )

  const onBulkDelete = useCallback(async (): Promise<void> => {
    const ids = [...selectedIds]
    if (ids.length === 0) return
    setBulkDeleting(true)
    let failed = 0
    await Promise.all(
      ids.map(id =>
        deleteVaultItemById(id).catch(() => {
          failed += 1
        }),
      ),
    )
    setBulkDeleting(false)
    setBulkConfirmOpen(false)
    setSelectedIds(new Set())
    await reload()
    showToast({
      titleKey: failed > 0 ? 'vault.list.bulk.partial_toast' : 'vault.list.bulk.deleted_toast',
      titleVars: { count: ids.length - failed },
      tone: failed > 0 ? 'danger' : 'success',
    })
  }, [selectedIds, reload, showToast])

  const goNew = useCallback((): void => {
    window.location.hash = 'vault/new'
  }, [])

  const goDetail = useCallback((id: string): void => {
    window.location.hash = `vault/${id}`
  }, [])

  const goEdit = useCallback((id: string): void => {
    window.location.hash = `vault/${id}/edit`
  }, [])

  /** item の所属フォルダ名を解決する (未所属 / 削除済は null)。 */
  const folderNameOf = (item: VaultItem): string | null =>
    item.folderId != null ? (folderById.get(item.folderId)?.name ?? null) : null

  /** ドメイングループの統合行 (クリックで展開 / 折りたたみ)。 */
  const renderGroupRow = (
    group: EquivalentDomainGroupWire,
    count: number,
    expanded: boolean,
  ): JSX.Element => (
    <li
      key={`group-${group.id}`}
      className={`list-row vault-list-row vault-group-row${expanded ? ' is-expanded' : ''}`}
      role="button"
      tabIndex={0}
      aria-expanded={expanded}
      aria-label={group.name}
      onClick={() => toggleGroup(group.id)}
      onKeyDown={ev => {
        if (ev.key === 'Enter' || ev.key === ' ') {
          ev.preventDefault()
          toggleGroup(group.id)
        }
      }}
      data-testid={`vault-group-${group.id}`}
    >
      <div className="vault-row-select" role="presentation" />
      <div className="vault-row-icon" aria-hidden="true">
        <Icon name="link" />
      </div>
      <div className="vault-row-main">
        <div className="nm">{group.name}</div>
        <div className="sb">{group.domains.join(' · ')}</div>
      </div>
      <div className="vault-row-tag">
        <span className="tag">{t('vault.list.group.count', { count })}</span>
      </div>
      <div className="vault-row-updated" />
      <div className="acts vault-row-acts vault-group-chevron" role="presentation">
        <Icon name={expanded ? 'chevron-down' : 'chevron-right'} />
      </div>
    </li>
  )

  const totalCount = state.kind === 'ready' ? state.items.length : 0
  const failureCount = state.kind === 'ready' ? state.failureCount : 0
  const isFiltered = filter !== 'all'
  const allSelected = filtered.length > 0 && filtered.every(it => selectedIds.has(it.id))

  return (
    <div>
      <PageHead
        title={t('vault.list.title')}
        sub={t('vault.list.subtitle', { count: totalCount })}
        actions={
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                window.location.hash = 'vault/trash'
              }}
              data-testid="vault-trash-link"
            >
              <Icon name="trash" aria-hidden="true" />
              {t('vault.trash.open_button')}
            </button>
            <button type="button" className="btn btn-primary" onClick={goNew}>
              <Icon name="plus" aria-hidden="true" />
              {t('vault.list.new_button')}
            </button>
          </>
        }
      />
      <div className="page-body flush">
        <div className="vault-filter-bar">
          <div className="vault-filter-chips" role="tablist" aria-label={t('vault.list.title')}>
            {VAULT_FILTER_VALUES.map(f => (
              <button
                key={f}
                type="button"
                role="tab"
                aria-selected={filter === f}
                className={`chip vault-filter-chip${filter === f ? ' active' : ''}`}
                onClick={() => setFilter(f)}
              >
                <Icon name={FILTER_ICON[f]} aria-hidden="true" />
                <span className="vault-filter-chip-label">{t(`vault.list.filter.${f}`)}</span>
                <span className="n">{counts[f]}</span>
              </button>
            ))}
          </div>

          <VaultFolderSelect
            folders={folders}
            activeFolderId={activeFolderId}
            onSelect={folderId => {
              window.location.hash = folderId === null ? 'vault' : `vault/folder/${folderId}`
            }}
            onCreate={() => {
              setFolderModalOpen(true)
              setFolderError(null)
            }}
            onManage={openManageFolders}
          />
        </div>

        {failureCount > 0 && (
          <div className="vault-decryption-warning">
            <Notice tone="warn" ico="alert">
              {t('vault.list.decryption_warning', { count: failureCount })}
            </Notice>
          </div>
        )}

        {state.kind === 'error' && (
          <div className="vault-state-message">
            <Notice tone="danger" ico="alert">
              {t(state.messageKey)}
            </Notice>
            <button type="button" className="btn btn-secondary" onClick={() => void reload()}>
              {t('vault.list.filter.all')}
            </button>
          </div>
        )}

        {state.kind === 'ready' && filtered.length === 0 && (
          <EmptyState
            icon="vault"
            title={t(isFiltered ? 'vault.list.empty.filtered_title' : 'vault.list.empty.title')}
            sub={t(isFiltered ? 'vault.list.empty.filtered_subtitle' : 'vault.list.empty.subtitle')}
            action={
              <button type="button" className="btn btn-primary" onClick={goNew}>
                <Icon name="plus" aria-hidden="true" />
                {t('vault.list.new_button')}
              </button>
            }
          />
        )}

        {state.kind === 'ready' && filtered.length > 0 && (
          <div className="vault-select-bar">
            <label className="vault-select-all">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={() =>
                  setSelectedIds(allSelected ? new Set() : new Set(filtered.map(it => it.id)))
                }
                data-testid="vault-select-all"
              />
              {t('vault.list.bulk.select_all')}
            </label>
            {selectedIds.size > 0 && (
              <div className="vault-select-actions">
                <span className="vault-select-count">
                  {t('vault.list.bulk.selected', { count: selectedIds.size })}
                </span>
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={() => setBulkConfirmOpen(true)}
                  data-testid="vault-bulk-delete"
                >
                  <Icon name="trash" aria-hidden="true" />
                  {t('vault.list.bulk.delete')}
                </button>
              </div>
            )}
          </div>
        )}

        {state.kind === 'ready' && filtered.length > 0 && (
          <ul className="list vault-list vault-list--selectable" role="list">
            {entries.flatMap(entry => {
              if (entry.kind === 'item') {
                return [
                  <VaultItemRow
                    key={entry.item.id}
                    item={entry.item}
                    nested={false}
                    isSelected={selectedIds.has(entry.item.id)}
                    folderName={folderNameOf(entry.item)}
                    duplicateSuffix={duplicateSuffixMap.get(entry.item.id) ?? ''}
                    onToggleSelect={toggleSelect}
                    onCopyUsername={onCopyUsername}
                    onCopyPassword={onCopyPassword}
                    onOpenDetail={goDetail}
                    onOpenEdit={goEdit}
                  />,
                ]
              }

              const expanded = expandedGroups.has(entry.group.id)

              const rows: JSX.Element[] = [
                renderGroupRow(entry.group, entry.items.length, expanded),
              ]

              if (expanded) {
                for (const it of entry.items) {
                  rows.push(
                    <VaultItemRow
                      key={it.id}
                      item={it}
                      nested
                      isSelected={selectedIds.has(it.id)}
                      folderName={folderNameOf(it)}
                      duplicateSuffix={duplicateSuffixMap.get(it.id) ?? ''}
                      onToggleSelect={toggleSelect}
                      onCopyUsername={onCopyUsername}
                      onCopyPassword={onCopyPassword}
                      onOpenDetail={goDetail}
                      onOpenEdit={goEdit}
                    />,
                  )
                }
              }

              return rows
            })}
          </ul>
        )}
      </div>

      {bulkConfirmOpen && (
        <div
          className="vault-delete-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="vault-bulk-delete-title"
        >
          <div className="vault-delete-dialog">
            <h3 id="vault-bulk-delete-title">{t('vault.list.bulk.confirm_title')}</h3>
            <p>{t('vault.list.bulk.confirm_body', { count: selectedIds.size })}</p>
            <div className="vault-delete-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setBulkConfirmOpen(false)}
                disabled={bulkDeleting}
              >
                {t('vault.list.bulk.cancel')}
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => void onBulkDelete()}
                disabled={bulkDeleting}
                data-testid="vault-bulk-delete-confirm"
              >
                <Icon name="trash" aria-hidden="true" />
                {bulkDeleting ? t('vault.list.bulk.deleting') : t('vault.list.bulk.confirm_submit')}
              </button>
            </div>
          </div>
        </div>
      )}

      {folderManageOpen && (
        <div
          className="vault-delete-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="folder-manage-title"
        >
          <div className="vault-delete-dialog">
            <h3 id="folder-manage-title">{t('vault.folder.manage_title')}</h3>
            <p>{t('vault.folder.manage_intro')}</p>
            {folders.length === 0 ? (
              <div className="vault-folder-manage-empty">{t('vault.folder.manage_empty')}</div>
            ) : (
              <div className="vault-folder-manage-list">
                {folders.map(folder => {
                  const busy = folderRowBusy[folder.id]
                  const draft = folderDrafts[folder.id] ?? folder.name
                  const dirty = draft.trim() !== folder.name && draft.trim().length > 0

                  return (
                    <div
                      key={folder.id}
                      className={`vault-folder-manage-row${busy === 'deleting' ? ' is-deleting' : ''}`}
                    >
                      <Icon name="folder" aria-hidden="true" />
                      <input
                        type="text"
                        className="input"
                        value={draft}
                        maxLength={80}
                        onChange={ev => {
                          const v = ev.target.value

                          setFolderDrafts(prev => ({ ...prev, [folder.id]: v }))
                        }}
                        aria-label={t('vault.folder.rename_label')}
                        data-testid={`vault-folder-manage-input-${folder.id}`}
                      />
                      <button
                        type="button"
                        className="icon-btn sm"
                        onClick={() => void onRenameFolder(folder.id)}
                        disabled={!dirty || busy != null}
                        title={
                          busy === 'saving' ? t('vault.folder.saving') : t('vault.folder.save')
                        }
                        aria-label={
                          busy === 'saving' ? t('vault.folder.saving') : t('vault.folder.save')
                        }
                        data-testid={`vault-folder-manage-save-${folder.id}`}
                      >
                        <Icon name="save" />
                      </button>
                      <button
                        type="button"
                        className="icon-btn sm"
                        onClick={() => setFolderDeleteTarget({ id: folder.id, name: folder.name })}
                        disabled={busy != null}
                        title={t('vault.folder.delete')}
                        aria-label={t('vault.folder.delete')}
                        data-testid={`vault-folder-manage-delete-${folder.id}`}
                      >
                        <Icon name="trash" />
                      </button>
                    </div>
                  )
                })}
              </div>
            )}
            {folderManageError !== null && (
              <Notice tone="danger" ico="alert">
                {t(folderManageError)}
              </Notice>
            )}
            <div className="vault-delete-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setFolderManageOpen(false)}
              >
                {t('vault.folder.manage_close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {folderDeleteTarget !== null && (
        <div
          className="vault-delete-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="folder-delete-title"
        >
          <div className="vault-delete-dialog">
            <h3 id="folder-delete-title">{t('vault.folder.delete_confirm_title')}</h3>
            <p>{t('vault.folder.delete_confirm_body', { name: folderDeleteTarget.name })}</p>
            <div className="vault-delete-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setFolderDeleteTarget(null)}
                disabled={folderRowBusy[folderDeleteTarget.id] === 'deleting'}
              >
                {t('vault.folder.delete_confirm_cancel')}
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => void onDeleteFolder(folderDeleteTarget.id)}
                disabled={folderRowBusy[folderDeleteTarget.id] === 'deleting'}
                data-testid="vault-folder-delete-confirm"
              >
                <Icon name="trash" aria-hidden="true" />
                {folderRowBusy[folderDeleteTarget.id] === 'deleting'
                  ? t('vault.folder.deleting')
                  : t('vault.folder.delete_confirm_submit')}
              </button>
            </div>
          </div>
        </div>
      )}

      {folderModalOpen && (
        <div
          className="vault-delete-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="folder-create-title"
        >
          <div className="vault-delete-dialog">
            <h3 id="folder-create-title">{t('vault.folder.create_title')}</h3>
            <p>{t('vault.folder.create_intro')}</p>
            <input
              type="text"
              className="input"
              value={folderDraft}
              onChange={ev => setFolderDraft(ev.target.value)}
              placeholder={t('vault.folder.name_placeholder')}
              maxLength={80}
              autoFocus
              data-testid="vault-folder-name-input"
            />
            {folderError !== null && (
              <Notice tone="danger" ico="alert">
                {t(folderError)}
              </Notice>
            )}
            <div className="vault-delete-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => {
                  setFolderModalOpen(false)
                  setFolderDraft('')
                  setFolderError(null)
                }}
                disabled={folderSubmitting}
              >
                {t('vault.folder.cancel')}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => void onCreateFolder()}
                disabled={folderSubmitting}
                data-testid="vault-folder-create"
              >
                <Icon name="plus" aria-hidden="true" />
                {folderSubmitting ? t('vault.folder.creating') : t('vault.folder.create_button')}
              </button>
            </div>
          </div>
        </div>
      )}

      <ScrollTopButton />
    </div>
  )
}
